import type { TypeInputState } from '~/types/common.types';

type TypeElementRefs<T> = {
  [K in keyof T]: HTMLElement | null;
};

type TypeStatus<T> = {
  [K in keyof T]: TypeInputState;
};

/**
 * 에러가 지정된 개수 이상일 때, 첫 번째 에러 필드로 포커스하는 유틸 함수
 *
 * @note Input 요소는 focus (disabled인 경우 scrollIntoView), 다른 요소는 scrollIntoView로 이동
 *
 * @param elementRefs - 각 필드의 DOM 요소 참조를 담고 있는 객체
 * @param status - 각 필드의 상태를 담고 있는 객체
 * @param focusOrder - 포커스 순서를 지정하는 키 배열 (기본값: status 내 순서)
 */
const focusFirstErrorField = <T extends object>(
  elementRefs: TypeElementRefs<T>,
  status: TypeStatus<T>,
  focusOrder?: (keyof T)[]
) => {
  const errorKeys = (Object.keys(status) as (keyof T)[]).filter((key) => status[key].status === 'error');

  if (errorKeys.length > 0) {
    let firstErrorKey: keyof T;

    // 포커스 순서가 지정되어 있으면 해당 순서대로 포커스, 없으면 기존 순서대로 포커스
    if (focusOrder && focusOrder.length > 0) {
      const sortedErrorKeys = focusOrder.filter((key) => errorKeys.includes(key));
      firstErrorKey = sortedErrorKeys.length > 0 ? sortedErrorKeys[0] : errorKeys[0];
    } else {
      firstErrorKey = errorKeys[0];
    }

    const targetElement = elementRefs[firstErrorKey];

    const isInputElement = targetElement instanceof HTMLInputElement || targetElement instanceof HTMLTextAreaElement;

    if (isInputElement && !targetElement.disabled) {
      targetElement.focus();
    } else {
      targetElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
};

export default focusFirstErrorField;
